import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { api } from '@/lib/axios'

/**
 * AP-16 — Heißt es hier „Projekt" oder „Auftrag"?
 *
 * Der Mandant legt das in den Grundeinstellungen fest
 * (`Tenant.settings.terminologyMode`); dieser Hook liefert die passenden
 * Begriffe. Er kombiniert zwei Achsen: die **Sprache** (i18n) und den
 * **Mandanten-Modus**. Die Wörter selbst stehen deshalb im Bereich
 * `terminology` beider Sprachdateien, nicht im Code.
 *
 * **Warum ganze Wörter statt Zusammensetzung:** Im Deutschen trägt „Auftrag"
 * ein Fugen-s („Auftragsnummer"), „Projekt" nicht („Projektnummer"). Ein Muster
 * wie `{{project}}nummer` ergäbe „Auftragnummer". Jede zusammengesetzte Form
 * steht deshalb ausgeschrieben in der Sprachdatei.
 *
 * **Nicht betroffen:** „Arbeitsschritt" gehört nicht zu dieser Achse. Und der
 * **Auftrags-Scan** heißt in beiden Modi so — gescannt wird ein
 * Produktionsauftrag für ein Projekt, also etwas anderes als das Datenobjekt,
 * um das es hier geht (Entscheidung des Auftraggebers, 6.9.2026).
 *
 * Der Modus wird modulweit gecacht, damit nicht jede Komponente einen eigenen
 * Request auslöst; beim Abmelden leert `resetTerminologyCache()` ihn (der Wert
 * gehört dem Mandanten, nicht dem Gerät — KA-11).
 */

export type TerminologyMode = 'PROJECT' | 'ORDER'

export interface Terms {
  mode: TerminologyMode
  /** „Projekt" / „Auftrag" */
  project: string
  /** „Projekte" / „Aufträge" */
  projectPlural: string
  /** „Projektverwaltung" / „Auftragsverwaltung" */
  management: string
  /** „Projektnummer" / „Auftragsnummer" */
  number: string
  /** „Projektname" / „Auftragsname" */
  name: string
  /** „Projektposition" / „Auftragsposition" */
  position: string
  /** „Projektpositionen" / „Auftragspositionen" */
  positionPlural: string
  /** „Nr. Projektposition" / „Nr. Auftragsposition" */
  positionNumber: string
  /** „IDProjektposition" / „IDAuftragsposition" */
  positionId: string
  /** „Projektdaten" / „Auftragsdaten" — Kategorie in der Filterleiste */
  data: string
  /** „Projekttagebuch" / „Auftragstagebuch" */
  diary: string
  /** „Projekt auswählen" / „Auftrag auswählen" */
  select: string
  /** „Kein Projekt" / „Kein Auftrag" */
  none: string
  /** „Keine Projekte vorhanden." / „Keine Aufträge vorhanden." */
  noneAvailable: string
  /** „Alle Projekte" / „Alle Aufträge" */
  all: string
  /** „Nach Projekt" / „Nach Auftrag" */
  by: string
  // AP-35 Detailblatt 1 9. (nachgezogen in Phase K) — je ein ganzes Wort:
  /** „Projektleitung" / „Auftragsleitung" */
  lead: string
  /** „Projekttyp" / „Auftragstyp" */
  type: string
  /** „Projektstatus" / „Auftragsstatus" */
  status: string
  /** „Sammelprojekt" / „Sammelauftrag" */
  collective: string
  /** „Projekt aus Beleg" / „Auftrag aus Beleg" */
  fromDocument: string
  /** „Projektvorlage" / „Auftragsvorlage" */
  template: string
  /** „Projektteilnehmer" / „Auftragsteilnehmer" */
  participants: string
  /** „Projektmappe" / „Auftragsmappe" — die Mappe der PWA (Blatt 10) */
  folder: string
  /** „Projektliste" / „Auftragsliste" — Menüpunkt und Absprung zur Liste (AP-35 Nachtrag 5) */
  list: string
}

let cachedMode: TerminologyMode | null = null
let inflight: Promise<TerminologyMode> | null = null

/**
 * Gemountete Komponenten, die über einen Moduswechsel Bescheid wissen wollen.
 *
 * Ohne sie zeigte die Navigation nach dem Umschalten weiter den alten Begriff:
 * Der Hook lädt den Modus beim Mounten, und ein geleerter Cache erreicht eine
 * Komponente nicht, die längst steht.
 */
const abonnenten = new Set<(mode: TerminologyMode) => void>()

/**
 * Der Request geht bewusst direkt über die geteilte Axios-Instanz statt über
 * `features/tenants` — eine Bibliothek unter `lib/` darf nicht in ein Feature
 * greifen (AP-21-B).
 */
async function loadMode(): Promise<TerminologyMode> {
  if (cachedMode) return cachedMode
  if (!inflight) {
    inflight = api
      .get<{ settings?: { terminologyMode?: string } }>('/tenants/me')
      .then(({ data }) => {
        cachedMode = data?.settings?.terminologyMode === 'ORDER' ? 'ORDER' : 'PROJECT'
        return cachedMode
      })
      .catch(() => 'PROJECT' as TerminologyMode)
      .finally(() => {
        inflight = null
      })
  }
  return inflight
}

/** Setzt den Cache zurück — nach Änderung der Einstellung und beim Abmelden. */
export function resetTerminologyCache() {
  cachedMode = null
  inflight = null
  // Neu laden und alle mitlaufenden Komponenten nachziehen.
  void loadMode().then((m) => abonnenten.forEach((melde) => melde(m)))
}

/** Synchroner Zugriff für Stellen außerhalb der Render-Phase (z. B. Exporte). */
export function getTerminologyMode(): TerminologyMode {
  return cachedMode ?? 'PROJECT'
}

export function useTerminology(): Terms {
  const { t } = useTranslation()
  const [mode, setMode] = useState<TerminologyMode>(cachedMode ?? 'PROJECT')

  useEffect(() => {
    let active = true
    const melde = (m: TerminologyMode) => {
      if (active) setMode(m)
    }
    abonnenten.add(melde)
    loadMode().then(melde)
    return () => {
      active = false
      abonnenten.delete(melde)
    }
  }, [])

  // Übersetzt wird bei jedem Rendern — so wirken Sprach- **und** Moduswechsel
  // sofort, statt bis zum nächsten Neuladen zu warten.
  const term = (key: string) => t(`terminology.${mode}.${key}`)

  return {
    mode,
    project: term('singular'),
    projectPlural: term('plural'),
    management: term('management'),
    number: term('number'),
    name: term('name'),
    position: term('position'),
    positionPlural: term('positionPlural'),
    positionNumber: term('positionNumber'),
    positionId: term('positionId'),
    data: term('data'),
    diary: term('diary'),
    select: term('select'),
    none: term('none'),
    noneAvailable: term('noneAvailable'),
    all: term('all'),
    by: term('by'),
    lead: term('lead'),
    type: term('type'),
    status: term('status'),
    collective: term('collective'),
    fromDocument: term('fromDocument'),
    template: term('template'),
    participants: term('participants'),
    folder: term('folder'),
    list: term('list'),
  }
}
